import { Component, OnInit, OnDestroy, PLATFORM_ID, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TvSeasonService } from '../../../../../core/services/tv/seasons/season-details.service';
import { TeaserTrailerService } from '../../../../../core/services/trailers.service';
import { YoutubePlayerService } from '../../../../../core/services/youtube.service';
import { TvDetailsService } from '../../../../../core/services/tv/tv.service';
import { CreditsService } from '../../../../../core/services/credits.service';
import { environment } from '../../../../../../environments/environment';
import { Utils } from '../../../../../shared/utils';
import ColorThief from 'colorthief';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-season-details',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './season-details.html',
  styleUrl: './season-details.scss'
})
export class SeasonDetails implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private sub?: Subscription;

  @ViewChild('castScrollContainer') castScrollContainer?: ElementRef<HTMLDivElement>;

  constructor(
    private seasonService: TvSeasonService,
    private tvDetailsService: TvDetailsService,
    private creditsService: CreditsService,
    private trailerService: TeaserTrailerService,
    private youtubeService: YoutubePlayerService,
    private route: ActivatedRoute,
    private router: Router,
    private utils: Utils
  ) {}

  // Configuration
  frontendUrl: string = environment.frontendUrl;
  backendUrl: string = environment.backendUrl;

  // Data
  seasonDetails: any = null;
  tvDetails: any = null;
  allSeasons: any[] = [];
  castList: any[] = [];
  crewList: any[] = [];
  videoList: any[] = [];

  // Routing IDs
  tvId: number = 0;
  seasonNumber: number = 1;
  totalSeasons: number = 1;
  showName: string = '';
  tvSlug: string = '';

  // UI state & Tabs (matching home trailer section)
  tabs: string[] = ['episodes', 'cast', 'videos', 'details'];
  activeTab: number = 0;
  isDropDown: boolean = false;
  backdropLoaded: boolean = false;

  // Trailer modal
  trailerId: string = '';
  showPlayer: boolean = false;

  // Dynamic colors
  dominantColor: string = '';
  palette: any[] = [];
  middleColor: string = 'rgba(18, 184, 134, 0.08)';

  ngOnInit(): void {
    this.sub = this.route.paramMap.subscribe(() => {
      this.getSeasonDetails();
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      if (this.youtubeService) {
        this.youtubeService.destroyPlayer();
      }
    }
  }

  /**
   * Fetch all data for this season & TV show
   */
  getSeasonDetails(): void {
    const slug = this.route.snapshot.paramMap.get('slug');
    this.tvSlug = slug || '';
    if (slug) {
      const [id] = slug.split('-');
      this.tvId = parseInt(id, 10);
    }

    const seasonId = this.route.snapshot.paramMap.get('id');
    if (seasonId) {
      this.seasonNumber = parseInt(seasonId, 10);
    }

    if (!this.tvId || !this.seasonNumber) return;

    // 1. Fetch TV show details (title, backdrop, all seasons list, genres)
    this.tvDetailsService.getTvDetails(this.tvId).subscribe({
      next: (data) => {
        const details = data?.result || data;
        this.tvDetails = details;
        this.showName = details?.name || details?.original_name || '';
        this.totalSeasons = details?.number_of_seasons || details?.seasons?.length || 1;
        this.allSeasons = (details?.seasons || []).filter((s: any) => s && s.season_number > 0);
      },
      error: (err) => console.error('Error loading TV show details:', err)
    });

    // 2. Fetch Season details (episodes list, air date, overview, poster)
    this.seasonService.getTvSeasonDetails(this.tvId, this.seasonNumber).subscribe({
      next: (data) => {
        this.seasonDetails = data?.result || data;
      },
      error: (err) => console.error('Error loading season details:', err)
    });

    // 3. Fetch Cast & Crew
    this.creditsService.getCredits(this.tvId, 'tv').subscribe({
      next: (data) => {
        this.castList = data?.cast || [];
        this.crewList = data?.crew || [];
      },
      error: (err) => console.error('Error loading credits:', err)
    });

    // 4. Fetch Trailers/Videos
    this.trailerService.getTeaserOrTrailers(this.tvId, 'tv').subscribe({
      next: (data) => {
        const results = data?.results || [];
        this.videoList = results;
        const officialTrailer = results.find((t: any) => t.type === 'Trailer' && t.official)
          || results.find((t: any) => t.type === 'Trailer')
          || results[0];
        if (officialTrailer) {
          this.trailerId = officialTrailer.key;
        }
      },
      error: (err) => console.error('Error loading trailers:', err)
    });
  }

  get backdropImageSrc(): string {
    const path = this.tvDetails?.backdrop_path || this.seasonDetails?.backdrop_path || this.seasonDetails?.poster_path;
    if (!path) return '';
    return `${this.backendUrl}/proxy?url=https://image.tmdb.org/t/p/original/${path}`;
  }

  get isFirstSeason(): boolean {
    return !this.seasonNumber || this.seasonNumber <= 1;
  }

  get isLastSeason(): boolean {
    return !this.seasonNumber || (this.totalSeasons ? this.seasonNumber >= this.totalSeasons : false);
  }

  /**
   * Switch tabs matching home trailer switcher
   */
  switchTab(index: number): void {
    this.activeTab = index;
    this.isDropDown = false;
  }

  selectTab(index: number): void {
    this.switchTab(index);
  }

  toggleDropdown(): void {
    this.isDropDown = !this.isDropDown;
  }

  getTabLabel(index: number): string {
    switch (index) {
      case 0:
        const epCount = this.seasonDetails?.episodes?.length || this.seasonDetails?.episode_count || 0;
        return epCount > 0 ? `Episodes (${epCount})` : 'Episodes';
      case 1:
        const castCount = this.castList?.length || 0;
        return castCount > 0 ? `Cast & Crew (${castCount})` : 'Cast & Crew';
      case 2:
        const vidCount = this.videoList?.length || 0;
        return vidCount > 0 ? `Videos (${vidCount})` : 'Videos';
      case 3:
        return 'Overview & Facts';
      default:
        return '';
    }
  }

  formattedPersonName(name: string): string {
    return this.utils.movieNameSplitter(name || '');
  }

  scrollCastLeft(): void {
    if (this.castScrollContainer?.nativeElement) {
      this.castScrollContainer.nativeElement.scrollBy({ left: -360, behavior: 'smooth' });
    }
  }

  scrollCastRight(): void {
    if (this.castScrollContainer?.nativeElement) {
      this.castScrollContainer.nativeElement.scrollBy({ left: 360, behavior: 'smooth' });
    }
  }

  navigateToEpisode(episode: any): void {
    if (!episode?.episode_number) return;
    const slug = this.tvSlug || this.tvId;
    this.router.navigate([`/tv/${slug}/season/${this.seasonNumber}/episode/${episode.episode_number}`]);
  }

  navigateToSeason(seasonNum: number): void {
    if (!seasonNum || seasonNum === this.seasonNumber) return;
    const slug = this.tvSlug || this.tvId;
    this.router.navigate([`/tv/${slug}/season/${seasonNum}`]);
  }

  onSeasonSelect(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const val = parseInt(select.value, 10);
    if (val && val !== this.seasonNumber) {
      this.navigateToSeason(val);
    }
  }

  previousSeason(): void {
    if (!this.isFirstSeason) {
      this.navigateToSeason(this.seasonNumber - 1);
    }
  }

  nextSeason(): void {
    if (!this.isLastSeason) {
      this.navigateToSeason(this.seasonNumber + 1);
    }
  }

  backToShow(): void {
    const slug = this.tvSlug || this.tvId;
    this.router.navigate([`/tv/${slug}`]);
  }

  playTrailer(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (!this.trailerId) {
      if (this.tvId) {
        this.trailerService.getTeaserOrTrailers(this.tvId, 'tv').subscribe({
          next: (data) => {
            const results = data?.results || [];
            const trailer = results.find((t: any) => t.type === 'Trailer' && t.official)
              || results.find((t: any) => t.type === 'Trailer')
              || results[0];
            if (trailer) {
              this.trailerId = trailer.key;
              this.startPlayer();
            }
          }
        });
      }
      return;
    }
    this.startPlayer();
  }

  private startPlayer(): void {
    this.showPlayer = true;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        this.youtubeService.playTrailer(this.trailerId, 'season-trailer-player');
      }, 300);
    }
  }

  closeTrailer(): void {
    this.showPlayer = false;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
      if (this.youtubeService) {
        this.youtubeService.destroyPlayer();
      }
    }
  }

  onImageLoad(img: HTMLImageElement): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      this.backdropLoaded = true;
      const colorThief = new ColorThief();
      const color = colorThief.getColor(img);
      this.dominantColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

      const palette = colorThief.getPalette(img, 5);
      this.palette = palette.map((c: any) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`);

      const middleIndex = Math.floor(this.palette.length / 2);
      this.middleColor = this.palette[middleIndex];
    } catch (error) {
      this.middleColor = 'rgba(18, 184, 134, 0.08)';
    }
  }

  onImageError(event: Event): void {
    this.utils.onImageVerticalError(event);
  }

  filterRating(rating: number): number {
    return this.utils.filerRatings(rating ?? 0);
  }

  getEpisodeDirector(episode: any): string | null {
    if (!episode?.crew?.length) return null;
    const director = episode.crew.find((c: any) => c.job === 'Director');
    return director ? director.name : null;
  }

  shareSeason(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const title = `${this.showName || 'TV Show'} - ${this.seasonDetails?.name || 'Season ' + this.seasonNumber}`;
    if (navigator.share) {
      navigator.share({
        title,
        text: this.seasonDetails?.overview || '',
        url: window.location.href
      }).catch(err => console.log('Share canceled', err));
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Season link copied to clipboard!');
      });
    }
  }
}
